<script setup lang="ts">
import ValidatedInput from "@/builder/components/input/ValidatedInput.vue";
import VueMultiselect from "vue-multiselect";
import { PropType, ref, watch, inject, computed, Ref } from "vue";
import { Address, LookupResponse } from "@/interfaces";
import { PageDesign } from "../../interfaces";
import { FieldStyle, PartialDisplayType } from "@/builder/enums";
import { useRenderStore } from "../../stores/renderStore";

const props = defineProps({
    id: {
        type: String,
        required: true,
    },
    name: {
        type: String,
        required: true,
    },
    address: {
        type: Object as PropType<Address>,
        required: true
    },
    showAddress2: {
        type: Boolean,
        required: false,
        default: true,
    },
    states: {
        type: Array as PropType<LookupResponse[]>,
        required: true
    },
    required: {
        type: Boolean,
        required: false,
        default: false,
    },
    address1Label: {
        type: String,
        required: false,
        default: "",
    },
    address1Placeholder: {
        type: String,
        required: false,
        default: "Address Line 1",
    },
    address2Label: {
        type: String,
        required: false,
        default: "",
    },
    address2Placeholder: {
        type: String,
        required: false,
        default: "Address Line 2",
    },
    cityLabel: {
        type: String,
        required: false,
        default: "",
    },
    cityPlaceholder: {
        type: String,
        required: false,
        default: "City",
    },
    stateLabel: {
        type: String,
        required: false,
        default: "",
    },
    statePlaceholder: {
        type: String,
        required: false,
        default: "State",
    },
    postalLabel: {
        type: String,
        required: false,
        default: "",
    },
    postalPlaceholder: {
        type: String,
        required: false,
        default: "Postal",
    },
    isShipping: {
        type: Boolean,
        required: false,
        default: false,
    }
});

const renderStore = useRenderStore();

const pageDesign = inject("pageDesign") as Ref<PageDesign>;
const fieldStyle = computed(() => {
    return pageDesign.value.fieldStyle;
});

const emit = defineEmits(['update-address']);

const stateSelection = ref(null as LookupResponse | null);

const address = ref(props.address);

const isModalDisplay = computed(() => {
    return renderStore.page.partialDisplayType === PartialDisplayType.Modal;
});

//watch for prop address state changes, map to state selection
watch(() => props.address.state, (newVal) => {
    //map to the state object
    const matchedState = props.states.find(x => x.value === newVal);
    if (matchedState) {
        stateSelection.value = matchedState;
    }
    else {
        stateSelection.value = null;
    }
},
    {
        deep: true
    });

//watch for state selection changes, map to address state
watch(() => stateSelection.value, (newVal) => {
    if (newVal) {
        address.value.state = newVal.value;
    }
});

//watch for props states to populate state selection
watch(() => props.states, (newVal) => {
    //map to the state object
    const matchedState = newVal.find(x => x.value === props.address.state);
    if (matchedState) {
        stateSelection.value = matchedState;
    }
});

//watch for changes to address, emit update
watch(() => address, (newVal) => {
    emit('update-address', newVal);
},
    {
        deep: true
    });

</script>

<template>
    <div class="form-row"
        :class="[{ 'rd-mt-4': fieldStyle != FieldStyle.Compact && !isModalDisplay }, { 'rd-mt-2.5': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]">
        <ValidatedInput :id="id" :value="address.address1" :label="address1Label" :accessibleLabel="address1Placeholder"
            class="rd-w-full" :name="name + '.Address.Address1'" :isRequired="required" :maxLength="75">
            <template #input="inputProps">
                <input type="text" v-model="address.address1" :placeholder="address1Placeholder"
                    :aria-describedby="inputProps.describedBy" :aria-required="inputProps.required"
                    :aria-invalid="inputProps.isInvalid" :id="id + '_' + name + '.Address.Address1'"
                    :autocomplete="isShipping ? 'shipping address-line1' : 'billing address-line1'" />
            </template>
        </ValidatedInput>
    </div>

    <div v-if="showAddress2" class="form-row"
        :class="[{ 'rd-mt-4': fieldStyle != FieldStyle.Compact && !isModalDisplay }, { 'rd-mt-2.5': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]">
        <ValidatedInput :id="id" :value="address.address2" :label="address2Label" :accessibleLabel="address2Placeholder"
            class="rd-w-full" :name="name + '.Address.Address2'" :maxLength="75">
            <template #input="inputProps">
                <input type="text" v-model="address.address2" :placeholder="address2Placeholder"
                    :aria-describedby="inputProps.describedBy" :aria-required="inputProps.required"
                    :aria-invalid="inputProps.isInvalid" :id="id + '_' + name + '.Address.Address2'"
                    :autocomplete="isShipping ? 'shipping address-line2' : 'billing address-line2'" />
            </template>
        </ValidatedInput>
    </div>

    <div class="form-row" :class="[{ 'rd-mt-0 md:rd-gap-x-4 md:rd-mt-4': fieldStyle != FieldStyle.Compact && !isModalDisplay },
    { 'rd-mt-0 md:rd-gap-x-4': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]">

        <ValidatedInput :id="id" :value="address.city" :label="cityLabel" accessibleLabel="Municipality"
            :name="name + '.Address.City'" class="rd-w-full md:rd-flex-equal" :class="[{ 'rd-mt-4 md:rd-mt-0': fieldStyle != FieldStyle.Compact && !isModalDisplay },
            { 'rd-mt-2.5': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]" :isRequired="required"
            :maxLength="50">
            <template #input="inputProps">
                <input type="text" v-model="address.city" placeholder="Municipality"
                    :aria-describedby="inputProps.describedBy" :aria-required="inputProps.required"
                    :aria-invalid="inputProps.isInvalid" :id="id + '_' + name + '.Address.City'"
                    :autocomplete="isShipping ? 'shipping address-level2' : 'billing address-level2'" />
            </template>
        </ValidatedInput>

        <ValidatedInput :id="id" :value="address.state" :label="stateLabel" accessibleLabel="Province/Territory"
            :name="name + '.Address.State'" class="rd-w-full md:rd-flex-equal" :class="[{ 'rd-mt-4 md:rd-mt-0': fieldStyle != FieldStyle.Compact && !isModalDisplay },
            { 'rd-mt-2.5': (fieldStyle != FieldStyle.Compact && isModalDisplay) },
            { 'md:rd-mt-[.3125rem]': (fieldStyle == FieldStyle.Underline && isModalDisplay) }]" :isRequired="required"
            :maxLength="50">
            <template #input="inputProps">
                <VueMultiselect :id="id + '_' + name + '.Address.State'" label="text" track-by="value" select-label=""
                    selected-label="" deselect-label="" class="multiselect-element" placeholder="Province/Territory"
                    :aria-describedby="inputProps.describedBy" :aria-required="inputProps.required"
                    :aria-invalid="inputProps.isInvalid" v-model="stateSelection" :options="states"
                    :autocomplete="isShipping ? 'shipping address-level1' : 'billing address-level1'" />
            </template>
        </ValidatedInput>

        <ValidatedInput :id="id" :value="address.postal" :label="postalLabel" :accessibleLabel="postalPlaceholder"
            :name="name + '.Address.Postal'" class="rd-w-full md:rd-flex-equal" :class="[{ 'rd-mt-4 md:rd-mt-0': fieldStyle != FieldStyle.Compact && !isModalDisplay },
            { 'rd-mt-2.5': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]" :isRequired="required"
            :maxLength="50" :is-canadian-postal="true">
            <template #input="inputProps">
                <input type="text" v-model="address.postal" placeholder="Postal Code"
                    :aria-describedby="inputProps.describedBy" :aria-required="inputProps.required"
                    :aria-invalid="inputProps.isInvalid" :id="id + '_' + name + '.Address.Postal'"
                    :autocomplete="isShipping ? 'shipping postal-code' : 'billing postal-code'" />
            </template>
        </ValidatedInput>
    </div>
</template>
